import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { AlertCircle, Cloud, RefreshCw } from '@/ui/icons'
import { useTrips, TripService } from '@/modules/trips'
import { useTripOverviewData } from '@/modules/trips/hooks/useTripOverviewData'
import { useAuth } from '@/modules/auth'
import { useSubscription, UpgradePrompt } from '@/modules/subscriptions'
import { TripsApi } from '@/shared/api/trips'
import { Button } from '@/ui/components'
import { Card, CardContent } from '@/ui/design-system'
import { AddRouteToTripModal } from '../modals/AddRouteToTripModal'
import { TripWeatherForecast } from '../TripWeatherForecast'
import { CampspotterWeatherCard, DEFAULT_EMPTY_MESSAGE } from '../CampspotterWeatherCard'
import type { CampspotterAssessment } from '@/shared/api'
import { MemberResponse } from '@/shared/api'
import type { WaypointResponse } from '@/shared/api/routes'
import { useDebouncedCallback } from '@/shared/hooks/useDebouncedCallback'
import { 
  formatWaypointLocationForMeetup, 
  findMeetupWaypoints, 
  normalizeTimeToHHMM
} from '../../utils/meetupWaypointSync'
import { cn } from '@/shared/utils'
import { OverviewTabSkeleton } from '@/ui/components'

interface TripOverviewTabProps {
  tripId: string
  members: MemberResponse[]
  trip: any
  isLoading: boolean
  onTripUpdate?: () => void
  isOwner?: boolean
  /** When true, hide the "Route and waypoints" placeholder (e.g. when rendered inline above itinerary). */
  hideRoutePlaceholder?: boolean
  /** Optional callback when user clicks Invite (e.g. scroll to crew section when tabs are removed). */
  onInvite?: () => void
}

export function TripOverviewTab({ tripId, members, trip, isLoading, onTripUpdate, isOwner = false, hideRoutePlaceholder = false, onInvite: onInviteProp }: TripOverviewTabProps) {
  const { updateTrip } = useTrips()
  const { getAccessToken } = useAuth()
  const { isPro } = useSubscription()
  const [isAddRouteModalOpen, setIsAddRouteModalOpen] = useState(false)
  const [weatherAssessments, setWeatherAssessments] = useState<CampspotterAssessment[]>([])
  const [isLoadingWeather, setIsLoadingWeather] = useState(false)
  const [weatherError, setWeatherError] = useState<string | null>(null)
  
  // Fetch trip overview data (waypoints and route schedule) in parallel
  const {
    waypoints: allWaypoints,
    tripWaypoints,
    routeSchedule,
    isLoading: isOverviewDataLoading,
    error: overviewDataError,
    fetchWaypoints,
    updateWaypoint,
    updateWaypointInState,
    refreshRouteSchedule,
    updateWaypointInSchedule,
    reorderWaypoints
  } = useTripOverviewData(tripId)
  
  // Create trips API instance for time updates
  const tripsApi = useMemo(() => new TripsApi(getAccessToken), [getAccessToken])
  
  // Flag to prevent sync loops (waypoint → trip only; meetup edit removed from this tab)
  const isSyncingFromWaypoint = useRef(false)
  
  // Debounced callback for refreshing waypoints after updates
  const debouncedRefreshWaypoints = useDebouncedCallback(() => {
    if (fetchWaypoints) {
      fetchWaypoints()
    }
  }, 500)

  // Sync meetup waypoint to trip meetup fields
  // Use refs for trip data to avoid recreating callback on every trip update
  const tripLocationRef = useRef<string | null>(null)
  const tripTimeRef = useRef<string | null>(null)
  
  useEffect(() => {
    tripLocationRef.current = trip?.meetup_location || null
    tripTimeRef.current = trip?.meetup_time || null
  }, [trip?.meetup_location, trip?.meetup_time])

  const syncMeetupWaypointToTrip = useCallback(async (waypoint: WaypointResponse) => {
    if (isSyncingFromWaypoint.current) {
      return // Prevent sync loops
    }

    if (waypoint.poi_type !== 'meetup_spot') {
      return
    }

    isSyncingFromWaypoint.current = true

    try {
      const location = formatWaypointLocationForMeetup(waypoint)
      const rawTime = waypoint.start_time || null

      // Normalize time to HH:MM format for backend validation
      // This handles cases where the database returns HH:MM:SS format
      const normalizedTime = rawTime ? normalizeTimeToHHMM(rawTime) : null

      // Use ref values to avoid dependency on trip object
      const currentLocation = tripLocationRef.current
      const currentTime = tripTimeRef.current

      // Normalize comparison - handle undefined vs null vs empty string
      const normalizedLocation = location || null
      const normalizedCurrentLocation = currentLocation || null
      const normalizedCurrentTime = currentTime || null

      const locationChanged = normalizedLocation !== normalizedCurrentLocation
      const timeChanged = normalizedTime !== normalizedCurrentTime

      // Only update if something actually changed
      if (locationChanged || timeChanged) {
        await updateTrip(tripId, {
          meetup_location: normalizedLocation || undefined,
          meetup_time: normalizedTime || undefined
        })

        // Update refs immediately to prevent duplicate syncs
        tripLocationRef.current = normalizedLocation
        tripTimeRef.current = normalizedTime
      }
    } catch (error) {
      // Don't show notification to user - this is a background sync
    } finally {
      // Reset flag after a delay to allow trip update to propagate
      // Use a longer delay to ensure the effect doesn't re-trigger
      setTimeout(() => {
        isSyncingFromWaypoint.current = false
      }, 1000)
    }
  }, [tripId, updateTrip])

  // Track previous waypoints to detect changes and prevent initial load sync
  const previousWaypointsRef = useRef<WaypointResponse[]>([])
  const hasInitializedRef = useRef(false)
  
  // Watch for meetup waypoint changes and sync to trip
  useEffect(() => {
    // Skip if still loading
    if (isOverviewDataLoading || isLoading || !trip) {
      // Reset initialization if we're back to loading state
      if (isOverviewDataLoading || isLoading) {
        hasInitializedRef.current = false
      }
      return
    }

    // Skip if we're currently syncing from waypoint (to prevent loops)
    if (isSyncingFromWaypoint.current) {
      return
    }

    // On first load after data is ready, just initialize tracking without syncing
    if (!hasInitializedRef.current) {
      previousWaypointsRef.current = allWaypoints
      hasInitializedRef.current = true
      return
    }

    // Only sync if waypoints actually changed in meaningful ways
    const waypointKey = (w: WaypointResponse) => `${w.id}-${w.poi_type}-${w.start_time}-${w.latitude}-${w.longitude}-${w.poi_address}`
    const currentKeys = allWaypoints.map(waypointKey).sort().join('|')
    const previousKeys = previousWaypointsRef.current.map(waypointKey).sort().join('|')

    if (currentKeys === previousKeys) {
      return // Waypoints haven't changed in any meaningful way
    }

    previousWaypointsRef.current = allWaypoints

    const meetupWaypoints = findMeetupWaypoints(allWaypoints)
    
    // Handle multiple meetup waypoints: keep only the first one, remove others
    if (meetupWaypoints.length > 1) {
      // Keep the first one, remove the rest
      const waypointsToRemove = meetupWaypoints.slice(1)
      waypointsToRemove.forEach(async (wp) => {
        try {
          // Remove poi_type to convert back to regular waypoint
          await tripsApi.updateWaypoint(tripId, wp.id, { poi_type: undefined })
          if (fetchWaypoints) {
            await fetchWaypoints()
          }
        } catch (error) {
        }
      })
      return
    }
    
    // Only sync if there's a meetup waypoint and it's different from what's in trip
    if (meetupWaypoints.length > 0) {
      const meetupWaypoint = meetupWaypoints[0]
      syncMeetupWaypointToTrip(meetupWaypoint)
    }
    // Note: We don't clear trip meetup fields if waypoint is removed to avoid accidental data loss
  }, [allWaypoints, syncMeetupWaypointToTrip, tripId, tripsApi, fetchWaypoints, isOverviewDataLoading, isLoading])

  const weatherWaypoints = useMemo(() => {
    const waypoints: Array<{
      id: string
      name: string
      latitude: number
      longitude: number
      poi_type: 'campsite' | 'meetup_spot'
    }> = []

    if (trip?.waypoints) {
      const waypointList = trip.waypoints.filter((wp: WaypointResponse) =>
        wp.poi_type && ['campsite', 'meetup_spot'].includes(wp.poi_type) &&
        wp.latitude && wp.longitude
      )
      waypoints.push(...waypointList.map((wp: WaypointResponse) => ({
        id: wp.id || `waypoint-${wp.latitude}-${wp.longitude}`,
        name: wp.name || 'Location',
        latitude: wp.latitude,
        longitude: wp.longitude,
        poi_type: wp.poi_type as 'campsite' | 'campground' | 'meetup_spot'
      })))
    }

    const hasMeetupWaypoint = waypoints.some(wp => wp.poi_type === 'meetup_spot')
    if (!hasMeetupWaypoint && trip?.meetup_location) {
      const coordMatch = trip.meetup_location.match(/^([+-]?\d+\.?\d*),\s*([+-]?\d+\.?\d*)$/)
      if (coordMatch) {
        const lat = parseFloat(coordMatch[1])
        const lon = parseFloat(coordMatch[2])
        if (!isNaN(lat) && !isNaN(lon)) {
          waypoints.push({
            id: `meetup-${lat}-${lon}`,
            name: trip.meetup_location,
            latitude: lat,
            longitude: lon,
            poi_type: 'meetup_spot'
          })
        }
      }
    }

    return waypoints
  }, [trip?.waypoints, trip?.meetup_location])

  const handleFetchWeather = useCallback(async () => {
    if (!tripId || !getAccessToken) return
    setIsLoadingWeather(true)
    setWeatherError(null)
    try {
      const tripService = new TripService(getAccessToken)
      const assessments = await tripService.fetchTripWeather(tripId)
      setWeatherAssessments(assessments)
    } catch (err) {
      setWeatherError("Weather couldn't be loaded. Try again.")
    } finally {
      setIsLoadingWeather(false)
    }
  }, [tripId, getAccessToken])

  useEffect(() => {
    if (
      isPro &&
      tripId &&
      trip?.start_date &&
      trip?.end_date &&
      weatherWaypoints.length > 0
    ) {
      handleFetchWeather()
    }
  }, [isPro, tripId, trip?.start_date, trip?.end_date, weatherWaypoints.length, handleFetchWeather])

  const handleRouteAdded = async () => {
    try {
      await refreshRouteSchedule(tripId)
      if (onTripUpdate) {
        onTripUpdate()
      }
    } catch (error) {
    }
  }

  if (isLoading) {
    return <OverviewTabSkeleton />
  }

  if (!trip) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-earth-900 dark:text-foreground mb-2">Trip Not Found</h3>
        <p className="text-earth-600 dark:text-earth-400">Unable to load trip details.</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">

      {/* Weather - Prominently displayed */}
      <div className="h-full flex flex-col">
        {isPro ? (
          <Card className="h-full">
            <CardContent className="pt-4">
              <div className="flex flex-row items-center justify-between gap-2 mb-2">
                <h3 className="text-base font-semibold text-earth-900 dark:text-foreground">
                  Weather Forecast
                </h3>
                {(trip?.start_date && trip?.end_date && weatherWaypoints.length > 0) && (
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleFetchWeather}
                    disabled={isLoadingWeather}
                  >
                    {isLoadingWeather ? (
                      'Loading…'
                    ) : (
                      <>
                        <RefreshCw className="w-4 h-4 mr-1" />
                        Refresh weather
                      </>
                    )}
                  </Button>
                )}
              </div>
              <CampspotterWeatherCard
                assessments={weatherAssessments}
                isLoading={isLoadingWeather}
                error={weatherError}
                emptyMessage={DEFAULT_EMPTY_MESSAGE}
              />
            </CardContent>
          </Card>
        ) : (
          <div className="h-full flex flex-col space-y-2">
            <TripWeatherForecast locations={weatherWaypoints} />
            <UpgradePrompt
              message="Get camping-specific forecasts and daily scores with Pro."
              currentTier="free"
              className="mt-2"
            />
          </div>
        )}
      </div>

      {overviewDataError && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 flex items-start gap-2">
          <AlertCircle className="w-5 h-5 mt-0.5" />
          <div>
            <div className="font-semibold text-sm">We had trouble loading the itinerary</div>
            <div className="text-xs">{overviewDataError}</div>
          </div>
        </div>
      )}

      <AddRouteToTripModal
        isOpen={isAddRouteModalOpen}
        onClose={() => setIsAddRouteModalOpen(false)}
        tripId={tripId}
        onRouteAdded={handleRouteAdded}
      />

    </div>
  )
}
